/**
 * Resolves the official referral code minted for the connected wallet.
 *
 * Context because both the dashboard's share card and the purchase form need the
 * same resolved code, and the lookup is keyed on the wallet — running it twice
 * would double the request on every account switch.
 */
import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { logger } from "@swarm/shared/logger";
import type { GetReferralCodeQuery } from "@swarm/shared/schemas";
import { useEffect, useState } from "react";

import { getReferralCodeByWalletAddress as fetchReferralCode } from "@/lib/api/referrals";
import { storeReferralCode } from "@/lib/referral";

import { ReferralsContext } from "./ReferralsContext";
import { ReferralsContextControllerProps, ReferralsContextType } from "./ReferralsContext.types";

export const ReferralsContextController = ({ children }: ReferralsContextControllerProps) => {
  const [officialReferralCode, setOfficialReferralCode] = useState<string | undefined>(undefined);

  const currentAccount = useCurrentAccount();

  async function getReferralCodeByWalletAddress({ address }: GetReferralCodeQuery) {
    try {
      const result = await fetchReferralCode({ address });

      if (!result.referralCode) return;

      // Validate even though this came from our own API: the value is persisted
      // and later signed into an on-chain transaction.
      if (!storeReferralCode(result.referralCode)) return;

      setOfficialReferralCode(result.referralCode);
    } catch (error) {
      logger.error(
        { address, err: error instanceof Error ? { message: error.message } : error },
        "failed to resolve referral code",
      );
    }
  }

  useEffect(() => {
    if (!currentAccount?.address) return;

    getReferralCodeByWalletAddress({ address: currentAccount.address });
  }, [currentAccount?.address]);

  const props: ReferralsContextType = {
    getReferralCodeByWalletAddress,
    officialReferralCode,
  };

  return <ReferralsContext.Provider value={props}>{children}</ReferralsContext.Provider>;
};
